import { useEffect, useState } from 'react';
import {
  GetCurrentStudyYear,
  GetOrganizationRegistrationsByOrganizationId,
  UpdateCollaborationDocument,
} from '../helper/apiHelper';
import type {
  OrganizationRegistration,
  ReducedOrganization,
} from '../Types/interfaces';
import { CollaborationType } from '../Types/interfaces';
import {
  Alert,
  Button,
  Col,
  Collapse,
  Divider,
  Form,
  InputNumber,
  List,
  Row,
  Typography,
} from 'antd';
import OrganizationElementComponent from './Organization/OrganizationElementComponent';
import { useTranslation } from 'react-i18next';

interface OrganizationRegistrationProcessComponentProps {
  userOrganization: ReducedOrganization;
}

export const OrganizationRegistrationProcessComponent = ({
  userOrganization,
}: OrganizationRegistrationProcessComponentProps) => {
  const [currentRegistration, setCurrentRegistration] =
    useState<OrganizationRegistration | null>();
  const { t } = useTranslation();

  const [
    archivedOrganizationRegistrations,
    setArchivedOrganizationRegistrations,
  ] = useState<Array<OrganizationRegistration>>();

  const [form] = Form.useForm();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const currentStudyYear = await GetCurrentStudyYear();
        const resp = await GetOrganizationRegistrationsByOrganizationId(
          userOrganization.id.toString()
        );

        const current = resp.find(
          (or) => or.collaborationDocument.studyYearId === currentStudyYear.id
        );

        if (current) {
          setCurrentRegistration(current);
          if (resp.length > 1) {
            const currentIndex = resp.indexOf(current);
            resp.splice(currentIndex, currentIndex);
            setArchivedOrganizationRegistrations(resp);
          } else {
            setArchivedOrganizationRegistrations([]);
          }
        } else {
          setArchivedOrganizationRegistrations(resp);
          setCurrentRegistration(null);
        }
      } catch (error) {
        console.error(error);
      }
    };
    fetchData();
  }, [userOrganization.id]);

  const handleDocumentUpdate = async () => {
    const studyYear = await GetCurrentStudyYear();
    await UpdateCollaborationDocument(userOrganization.id.toString(), {
      studyYearId: studyYear.id,
      registrationDate: '',
      registrationNumber: '',
      collaborationType: CollaborationType.AnualAgreement,
      bachelorStudentCount: form.getFieldValue('bachelorStudentCount'),
      masterStudentCount: form.getFieldValue('masterStudentCount'),
    });

    const fetchData = async () => {
      try {
        const currentStudyYear = await GetCurrentStudyYear();
        const resp = await GetOrganizationRegistrationsByOrganizationId(
          userOrganization.id.toString()
        );
        const current = resp.find(
          (or) => or.collaborationDocument.studyYearId === currentStudyYear.id
        );

        if (!current) {
          console.error('current is undefined');
          return;
        }

        setCurrentRegistration(current);
        const currentIndex = resp.indexOf(current);
        if (currentIndex === 0) {
          setArchivedOrganizationRegistrations([]);
        } else {
          resp.splice(currentIndex, currentIndex);
          setArchivedOrganizationRegistrations(resp);
        }
      } catch (error) {
        console.error(error);
      }
    };
    fetchData();
  };

  return (
    <div>
      <Divider />
      <Typography.Text style={{ marginLeft: '20px', fontSize: '25px' }}>
        {userOrganization.name}
      </Typography.Text>
      {!userOrganization.isUniversityOrganization && (
        <div style={{ margin: '20px' }}>
          {currentRegistration !== null ? (
            <OrganizationElementComponent
              isArchived={false}
              organizationRegistration={currentRegistration}
              isUniversityOrganization={
                userOrganization.isUniversityOrganization
              }
            />
          ) : (
            <div>
              <Alert
                message={t('orgElementComp.alertMessage')}
                description={t('orgElementComp.alertDescription')}
                type="warning"
                showIcon
                style={{ marginBottom: '20px' }}
              />
              <Typography.Text style={{ fontSize: '20px' }}>
                {t('orgRegComp.addStudentCount')}
              </Typography.Text>
              <Form
                style={{ alignItems: 'center', justifyContent: 'center' }}
                form={form}
                onFinish={handleDocumentUpdate}
              >
                <Row
                  style={{ marginTop: 20 }}
                  justify={'center'}
                  gutter={[64, 0]}
                >
                  <Col sm={8}>
                    <Form.Item
                      name={'bachelorStudentCount'}
                      rules={[
                        {
                          required: true,
                          message: t('orgElementComp.numberNeeded'),
                        },
                      ]}
                    >
                      <InputNumber
                        style={{ width: '100%' }}
                        placeholder={t('orgElementComp.bachelorCount')}
                      />
                    </Form.Item>
                  </Col>
                  <Col sm={8}>
                    <Form.Item
                      name={'masterStudentCount'}
                      rules={[
                        {
                          required: true,
                          message: t('orgElementComp.numberNeeded'),
                        },
                      ]}
                    >
                      <InputNumber
                        style={{ width: '100%' }}
                        placeholder={t('orgElementComp.masterCount')}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row style={{ justifyContent: 'center' }}>
                  <Form.Item>
                    <Button type="primary" htmlType="submit">
                      {t('orgElementComp.updateDocument')}
                    </Button>
                  </Form.Item>
                </Row>
              </Form>
            </div>
          )}
        </div>
      )}
      {archivedOrganizationRegistrations &&
        archivedOrganizationRegistrations.length !== 0 && (
          <div style={{ margin: '20px' }}>
            <Collapse
              items={[
                {
                  key: 'archive',
                  label: t('orgElementComp.archivedDocuments'),
                  children: (
                    <List
                      bordered
                      size="large"
                      dataSource={archivedOrganizationRegistrations}
                      renderItem={(item) => {
                        return (
                          <OrganizationElementComponent
                            isArchived={true}
                            organizationRegistration={item}
                            isUniversityOrganization={
                              userOrganization.isUniversityOrganization
                            }
                          />
                        );
                      }}
                    />
                  ),
                },
              ]}
            />
          </div>
        )}
    </div>
  );
};
